import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  useTheme,
  Pagination,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Delete,
  CheckCircle,
  Error,
  HourglassEmpty,
  Visibility,
  ExpandMore,
} from "@mui/icons-material";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { format } from "date-fns";
import RagDocumentViewer from "./RagDocumentViewer";
import { formatSourceType, formatFileSize } from "../utils/formatters";
import { LinkDocumentMenu } from "./LinkDocumentMenu";

const RagDocumentUploadHistory = ({
  uploads,
  loading,
  page,
  pageInfo,
  onPageChange,
  onRefetch,
  onDialogStateChange,
}) => {
  const ApiClient = useApiClient();
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [linkDialogState, setLinkDialogState] = useState({});

  // Notify parent when any link dialog is open
  useEffect(() => {
    const hasAnyDialogOpen = Object.values(linkDialogState).some((dialogs) =>
      Object.values(dialogs).some((isOpen) => isOpen === true),
    );
    onDialogStateChange?.(hasAnyDialogOpen);
  }, [linkDialogState, onDialogStateChange]);

  const handleViewClick = (node) => {
    setViewingDocument(node);
    setViewerOpen(true);
  };

  const handleDeleteClick = (node) => {
    setSelectedUpload(node);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUpload) return;

    setDeleting(true);
    try {
      const response = await ApiClient.rag.deleteDocument({
        ragUpload: selectedUpload.id,
      });

      if (response.success) {
        enqueueSnackbar(response.message || "Document deleted successfully", {
          variant: "success",
        });
        setDeleteDialogOpen(false);
        setSelectedUpload(null);
        onRefetch();
      } else {
        enqueueSnackbar(response.message || "Failed to delete document", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      enqueueSnackbar("Error deleting document", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      Completed: {
        label: "Completed",
        color: "success",
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
      },
      Submitted: {
        label: "Submitted",
        color: "default",
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
      },
      Failed: {
        label: "Failed",
        color: "error",
        icon: <Error sx={{ fontSize: 16 }} />,
      },
      "In Progress": {
        label: "Processing",
        color: "info",
        icon: <HourglassEmpty sx={{ fontSize: 16 }} />,
      },
      Pending: {
        label: "Pending",
        color: "default",
        icon: <HourglassEmpty sx={{ fontSize: 16 }} />,
      },
    };

    const config = statusConfig[status] || statusConfig["Pending"];

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  if (loading && uploads.length === 0) {
    return (
      <Card sx={{ p: 3, textAlign: "center" }}>
        <CardContent>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading documents...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const rootDocuments = uploads.filter((doc) => !doc.parent);

  if (!loading && rootDocuments.length === 0) {
    return (
      <Card sx={{ p: 3, textAlign: "center" }}>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            No documents uploaded yet. Click "Upload Document" to get started.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Recursive component for rendering document tree
  const DocumentTreeItem = ({ document, level = 0 }) => {
    const hasChildren = document.children && document.children.length > 0;
    const canAddChildren = document.canHaveChildren;
    const isFailed = document.job?.status === "Failed";
    const isExpanded = expandedAccordions.has(document.id);

    const handleAccordionChange = (event, newExpanded) => {
      setExpandedAccordions((prev) => {
        const next = new Set(prev);
        if (newExpanded) {
          next.add(document.id);
        } else {
          next.delete(document.id);
        }
        return next;
      });
    };

    return (
      <Accordion
        expanded={isExpanded}
        onChange={handleAccordionChange}
        sx={{
          backgroundColor:
            level === 0
              ? theme.palette.background.paper
              : theme.palette.background.default,
          "&:before": { display: "none" },
          ...(level === 2 && { mt: 1 }),
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            "& .MuiAccordionSummary-content": {
              my: 1,
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              visibility: hasChildren ? "visible" : "hidden",
              pointerEvents: hasChildren ? "auto" : "none",
            },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ width: "100%", pr: 2 }}
          >
            {/* Filename */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexBasis: 250,
                flexGrow: 2.5,
                minWidth: 150,
              }}
            >
              <Typography variant="body1" noWrap>
                {document.originalFilename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {document.fileType.toUpperCase()} •{" "}
                {formatFileSize(document.fileSize)}
              </Typography>
            </Box>

            {/* Source Type */}
            <Box
              sx={{
                flexBasis: 140,
                flexGrow: 1,
                minWidth: 120,
              }}
            >
              <Chip
                label={formatSourceType(document.sourceType)}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Status */}
            <Box
              sx={{
                flexBasis: 120,
                flexGrow: 1,
                minWidth: 100,
              }}
            >
              {getStatusChip(document.job?.status)}
            </Box>

            {/* Upload Date */}
            <Box
              sx={{
                flexBasis: 150,
                flexGrow: 1.5,
                minWidth: 120,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {format(new Date(document.createdAt), "MMM d, yyyy HH:mm")}
              </Typography>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                flexBasis: 150,
                flexGrow: 1,
                minWidth: 120,
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              {canAddChildren && (
                <LinkDocumentMenu
                  parentDocument={document}
                  allowedChildTypes={document.allowedChildTypes}
                  onUploadSuccess={onRefetch}
                  disabled={isFailed}
                  openDialogs={linkDialogState[document.id] || {}}
                  setOpenDialogs={(dialogs) => {
                    setLinkDialogState((prev) => ({
                      ...prev,
                      [document.id]: dialogs,
                    }));
                  }}
                />
              )}
              <Tooltip title="View details">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick(document);
                  }}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(document);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </AccordionSummary>

        {hasChildren && (
          <AccordionDetails sx={{ pt: 1, pb: 1 }}>
            <Stack spacing={1} sx={{ pl: 2 }}>
              {document.children.map((child) => (
                <DocumentTreeItem
                  key={child.id}
                  document={child}
                  level={level + 1}
                />
              ))}
            </Stack>
          </AccordionDetails>
        )}
      </Accordion>
    );
  };

  return (
    <>
      <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Typography variant="h5" mb={2}>
            Uploaded Documents
          </Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              maxHeight: 600,
              overflowY: "auto",
              backgroundColor: theme.palette.background.default,
              borderRadius: 1,
              p: 1,
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[400],
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            <Stack direction="column" spacing={1}>
              {rootDocuments.map((doc) => (
                <DocumentTreeItem key={doc.id} document={doc} level={0} />
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          page={page}
          onChange={onPageChange}
          count={pageInfo.hasNextPage ? page + 1 : page}
          color="primary"
          disabled={loading}
        />
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedUpload?.originalFilename}
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <RagDocumentViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        document={viewingDocument}
        onRefetch={onRefetch}
      />
    </>
  );
};

export default RagDocumentUploadHistory;
